/**
 * Macro #23 — Growth & Influence Rewards API client (owner-facing).
 *
 * All monetary + influence values are BACKEND-AUTHORITATIVE. The frontend never
 * fabricates earnings or Vault liquidity. Writes (withdraw / withdraw-all /
 * request-payout) carry the SIWE session cookie + the session-bound CSRF token
 * (shared memory-only provider) exactly like the other owner mutations.
 */
import { API_BASE } from "./api";
import { getCsrfToken, clearCsrfToken } from "./territory-identity-api";

export class RewardsError extends Error {
  code: string;
  status: number;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "RewardsError";
    this.status = status;
    this.code = code;
  }
}

async function parse<T>(resp: Response): Promise<T> {
  const text = await resp.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }
  if (!resp.ok) {
    const env = (body ?? {}) as { code?: string; message?: string; detail?: { code?: string; message?: string } };
    const code = env.code ?? env.detail?.code ?? "UNKNOWN";
    const message = env.message ?? env.detail?.message ?? resp.statusText;
    throw new RewardsError(resp.status, code, message);
  }
  return body as T;
}

async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  const resp = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    signal,
  });
  return parse<T>(resp);
}

async function csrfPost<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const doPost = async (csrf: string) =>
    fetch(`${API_BASE}${path}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", "X-The-Grid-CSRF": csrf },
      body: JSON.stringify(body),
    });
  let resp = await doPost(await getCsrfToken());
  if (resp.status === 403) {
    const peek = await resp.clone().json().catch(() => null);
    const c = peek?.code ?? peek?.detail?.code;
    if (c === "CSRF_INVALID" || c === "CSRF_REQUIRED") {
      clearCsrfToken();
      resp = await doPost(await getCsrfToken(true));
    }
  }
  return parse<T>(resp);
}

// ---- Types (wire numbers are integer micro-USDC) ----
export type FundingState = "PENDING_FUNDING" | "FUNDED";
export type WithdrawalStatus = "WITHDRAWAL_PENDING" | "WITHDRAWN" | "FAILED";

export interface NeighborProvenance {
  qualified_edge_length: number;
  total_qualified_edge_length: number;
  pool_micro?: number;
  dust_micro?: number;
  pool_points?: number;
}

export interface RewardRecord {
  id: string;
  reward_type: "REFERRAL" | "NEIGHBOR";
  gross_reward_micro: number;
  basis_bps: number;
  gross_acquisition_micro: number;
  funding_state: FundingState;
  funded_at: string | null;
  source_acquisition_id: string;
  tx_hash: string | null;
  parcel_ids: number[];
  neighbor_provenance: NeighborProvenance | null;
  qualification_time: string | null;
}

export interface WithdrawalRecord {
  id: string;
  gross_micro: number;
  fee_micro: number;
  estimated_gas_micro: number;
  net_micro: number;
  status: WithdrawalStatus;
  failure_reason: string | null;
  created_at: string | null;
}

export interface InfluenceSummary {
  wallet: string;
  influence_points: number;
  badge: string | null;
  by_role: Record<string, number>;
  next_badge: string | null;
  next_badge_threshold: number | null;
  points_to_next: number;
  progress_pct: number;
}

export interface RewardsSelf {
  wallet: string;
  micro_per_usdc: number;
  pending_funding_micro: number;
  available_micro: number;
  withdrawal_pending_micro: number;
  withdrawn_gross_micro: number;
  expired_micro: number;
  timer_deadline: string | null;
  min_withdrawal_micro: number;
  withdrawal_enabled: boolean;
  history: RewardRecord[];
  withdrawals: WithdrawalRecord[];
  influence: InfluenceSummary;
}

export interface WithdrawalResult {
  id: string;
  status: WithdrawalStatus;
  gross_micro: number;
  fee_micro: number;
  estimated_gas_micro: number;
  net_micro: number;
}

export interface PayoutResult {
  status: string;
  idempotent: boolean;
  id: string;
}

// ── Admin rewards dashboard (read-only) ──────────────────────────────────────
export interface AdminPayoutRequest {
  id: string;
  wallet: string;
  created_at: string;
}

export interface AdminRewardsDashboard {
  activation: {
    activated: boolean;
    activation_block: number | null;
    activated_at: string | null;
  };
  liabilities: {
    pending_funding_micro: number;
    funded_available_micro: number;
    withdrawal_pending_micro: number;
    withdrawn_gross_micro: number;
    expired_forfeited_micro: number;
  };
  vault: {
    balance_micro: number;
    deposits_micro: number;
    service_fees_micro: number;
    admin_withdrawn_surplus_micro: number;
    funded_liability_micro: number;
    free_surplus_micro: number;
    coverage_pct: number;
    required_top_up_micro: number;
  };
  breakdown: {
    referral_liability_micro: number;
    neighbor_liability_micro: number;
    service_fees_accumulated_micro: number;
    expired_recoverable_micro: number;
  };
  fifo_queue: Array<{
    id: string;
    reward_type: string;
    beneficiary_wallet: string;
    gross_reward_micro: number;
    order_block: number;
    order_log_index: number;
  }>;
  withdrawal_counts: Record<string, number>;
  payout_requests: AdminPayoutRequest[];
}

export const rewardsApi = {
  self: (signal?: AbortSignal) => get<RewardsSelf>("/api/v1/rewards/self", signal),
  adminDashboard: (signal?: AbortSignal) =>
    get<AdminRewardsDashboard>("/api/v1/admin/rewards/dashboard", signal),
  influenceSelf: (signal?: AbortSignal) => get<InfluenceSummary>("/api/v1/influence/self", signal),
  withdraw: (amountMicro: number, estimatedGasMicro?: number) =>
    csrfPost<WithdrawalResult>("/api/v1/rewards/withdraw", {
      amount_micro: amountMicro,
      ...(estimatedGasMicro != null ? { estimated_gas_micro: estimatedGasMicro } : {}),
    }),
  withdrawAll: (estimatedGasMicro?: number) =>
    csrfPost<WithdrawalResult>("/api/v1/rewards/withdraw-all", {
      ...(estimatedGasMicro != null ? { estimated_gas_micro: estimatedGasMicro } : {}),
    }),
  requestPayout: () => csrfPost<PayoutResult>("/api/v1/rewards/request-payout", {}),
  resolvePayout: (id: string, decision: "RESOLVED" | "REJECTED", note?: string) =>
    csrfPost<{ id: string; status: string; resolved_at: string; admin_note: string | null }>(
      `/api/v1/admin/rewards/payouts/${encodeURIComponent(id)}/resolve`,
      { decision, ...(note && note.trim() ? { note: note.trim() } : {}) },
    ),
};

/**
 * Parse a human USDC decimal string into integer micro-USDC (bigint), no float.
 * Accepts up to 6 fractional digits. Returns null on malformed/negative input.
 */
export function parseUsdcToMicro(input: string): bigint | null {
  const s = input.trim();
  if (!/^\d+(\.\d{1,6})?$/.test(s)) return null;
  const [whole, frac = ""] = s.split(".");
  const fracPadded = (frac + "000000").slice(0, 6);
  try {
    return BigInt(whole) * 1_000_000n + BigInt(fracPadded);
  } catch {
    return null;
  }
}

/** Deterministic 2% service fee on a gross micro amount (floor), integer-safe. */
export function serviceFeeMicro(grossMicro: bigint, feeBps = 200): bigint {
  if (grossMicro <= 0n) return 0n;
  return (grossMicro * BigInt(feeBps)) / 10_000n;
}
